import { prettifySalesCategory } from "api-types/builders/salesCategory";
import { assertIsNotUndefined, ensureDefined, ensureIsBoolean, ensureIsString } from "common/utils/assertOfType";
import stringApplySearch from "common/utils/stringApplySearch";
import { camelCaseToTitleCase } from "common/utils/strings";
import type { BuilderModalStep } from "lib/components/builders/communities/plans/modals/BuilderModalStepped";
import BuilderModalStepped from "lib/components/builders/communities/plans/modals/BuilderModalStepped";
import { planLibraryActions } from "lib/components/builders/plan-library/planLibraryReducer";
import { usePlanLibraryDispatch, usePlanLibrarySelector } from "lib/components/builders/plan-library/planLibraryStore";
import type { PlanLibraryOptionSet } from "lib/components/builders/plan-library/planLibraryTypes";
import { salesCategorySelectItems } from "lib/state/salesCategory";
import { useState } from "react";
import Checkbox from "ui/forms/CheckboxOld";
import InputGroupText from "ui/forms/InputGroupText";
import Select from "ui/forms/Select";
import useAppContext from "ui/hooks/useAppContext";
import styles from "./PlanLibraryMassOverrideModal.module.scss";
import modalStyles from "./PlanLibraryModal.module.scss";

interface MassEditModalState {
  search: string;
  optionSetIds: string[];
  overrideKey?: string;
  overrideVal?: string | boolean;
}

export default function PlanLibraryMassOverrideModal() {
  const { setUserNotification } = useAppContext();
  const dispatch = usePlanLibraryDispatch();

  const {
    nonBoolOptionSetsPerSalesCategory,
    booleanOptionSetsPerSalesCategory,
    salesCategoryCustomization,
    salesCategoryNames,
    massEditing,
  } = usePlanLibrarySelector((state) => ({
    nonBoolOptionSetsPerSalesCategory: state.nonBoolOptionSetsPerSalesCategory,
    booleanOptionSetsPerSalesCategory: state.booleanOptionSetsPerSalesCategory,
    salesCategoryCustomization: state.builder.salesConfig?.salesCategoryCustomization,
    salesCategoryNames: state.salesCategoryNames,
    massEditing: state.massEditing,
  }));

  const optionSets = salesCategoryNames.reduce<PlanLibraryOptionSet[]>((optionSets, category) => {
    return optionSets.concat(nonBoolOptionSetsPerSalesCategory[category], booleanOptionSetsPerSalesCategory[category]);
  }, []);

  const [modalState, setModalState] = useState<MassEditModalState>({ optionSetIds: [], search: "" });

  function getPrettyOverrideVal(key: string | undefined, val: string | boolean | undefined): string {
    switch (key) {
      case "salesCategory":
        return ensureDefined(prettifySalesCategory(val as string, salesCategoryCustomization));
      case "disabled":
        return val ? "true" : "false";
      default:
        return "n/a";
    }
  }

  const modalSteps: BuilderModalStep[] = [
    {
      subheader: "Select the option sets you'd like to override",
      header: "Mass Override Options",
      body: (
        <>
          <div className={styles.searchWrapper}>
            <InputGroupText
              onChange={(search) => setModalState({ ...modalState, search })}
              placeholder="Search Option Sets..."
              inputValue={modalState.search}
              labelText=""
              type="text"
            />
          </div>

          <div className={styles.optionSetSelectionWrapper}>
            {optionSets
              .filter((optionSet) => stringApplySearch(optionSet.name, modalState.search))
              .map((optionSet) => (
                <div key={optionSet.id} className={styles.optionSetRow}>
                  <Checkbox
                    onChange={() => {
                      if (modalState.optionSetIds?.includes(optionSet.id)) {
                        const optionSetIds = modalState.optionSetIds.filter((id) => id !== optionSet.id);
                        setModalState({ ...modalState, optionSetIds });
                      } else {
                        const optionSetIds = modalState.optionSetIds.concat(optionSet.id);
                        setModalState({ ...modalState, optionSetIds });
                      }
                    }}
                    checked={modalState.optionSetIds.includes(optionSet.id)}
                  />
                  {optionSet.name}
                </div>
              ))}
          </div>
        </>
      ),
      submittable: modalState.optionSetIds.length > 0,
    },

    {
      subheader: "Choose the field you'd like to override in the selected option sets",
      header: "Mass Override Options",
      body: (
        <>
          <div className={modalStyles.fieldWrapper}>
            <div className={modalStyles.labelWrapper}>
              <label>Name of Field</label>
            </div>
            <Select
              placeholder="i.e. Sales Category"
              value={modalState.overrideKey}
              options={[
                { label: "Sales Category", value: "salesCategory" },
                { label: "Disabled", value: "disabled" },
              ]}
              onChange={(value) =>
                setModalState({ ...modalState, overrideKey: ensureIsString(value), overrideVal: undefined })
              }
            />
          </div>
          {modalState.overrideKey && (
            <div className={modalStyles.fieldWrapper}>
              <div className={modalStyles.labelWrapper}>
                <label>Override Value</label>
              </div>
              {(() => {
                switch (modalState.overrideKey) {
                  case "salesCategory":
                    return (
                      <Select
                        placeholder="Select sales category"
                        value={modalState.overrideVal as string}
                        options={salesCategorySelectItems(salesCategoryCustomization)}
                        onChange={(value) => setModalState({ ...modalState, overrideVal: ensureIsString(value) })}
                      />
                    );
                  case "disabled":
                    return (
                      <Select
                        placeholder="false (enable), true (disable)"
                        value={modalState.overrideVal as boolean}
                        options={[
                          { label: "false", value: false },
                          { label: "true", value: true },
                        ]}
                        onChange={(value) => setModalState({ ...modalState, overrideVal: ensureIsBoolean(value) })}
                      />
                    );
                  default:
                    return null;
                }
              })()}
            </div>
          )}
        </>
      ),
      submittable: modalState.overrideKey !== undefined && modalState.overrideVal !== undefined,
    },

    {
      header: "Mass Override Options",
      subheader: (
        <>
          You are about to apply the override{" "}
          <b>{getPrettyOverrideVal(modalState.overrideKey, modalState.overrideVal)}</b> to the{" "}
          <b>{camelCaseToTitleCase(modalState.overrideKey)}</b> field for the following {modalState.optionSetIds.length}{" "}
          option sets{" "}
        </>
      ),
      body: (
        <div className={styles.optionSetReviewWrapper}>
          <span className={styles.currentValueLabel}>
            Before Change ({camelCaseToTitleCase(modalState.overrideKey)})
          </span>
          <div className={styles.optionSetSelectionWrapper} style={{ height: "unset" }}>
            {optionSets
              .filter(({ id }) => modalState.optionSetIds?.includes(id))
              .map((optionSet) => (
                <div key={optionSet.id} className={styles.optionSetRow} style={{ justifyContent: "space-between" }}>
                  <span>{optionSet.name}</span>
                  <span className={styles.currentValue}>
                    {getPrettyOverrideVal(modalState.overrideKey, optionSet[modalState.overrideKey ?? ""] as any)}
                  </span>
                </div>
              ))}
          </div>
        </div>
      ),
      submittable: true,
    },
  ];

  return (
    <BuilderModalStepped
      steps={modalSteps}
      onSubmit={() => {
        for (const optionSetId of modalState.optionSetIds) {
          const oldOptionSet = optionSets.find((optionSet) => optionSet.id === optionSetId);
          assertIsNotUndefined(oldOptionSet);
          const newOptionSet = { ...oldOptionSet, [modalState.overrideKey ?? ""]: modalState.overrideVal };
          dispatch(planLibraryActions.overrideOptionSet({ oldOptionSet, newOptionSet }));
        }
        setUserNotification({
          message: `${modalState.optionSetIds.length} option sets have been updated`,
          messageType: "success",
        });
        dispatch(planLibraryActions.enableMassEditModal(false));
      }}
      modalProps={{
        onClose: () => dispatch(planLibraryActions.enableMassEditModal(false)),
        open: massEditing,
      }}
    />
  );
}
